import Reveal from "@/components/base/Reveal";
import { whatsappLink } from "@/constants/site";

interface DemoBannerProps {
  message?: string;
}

/**
 * Destaque do "site de demonstração sem custo": a empresa vê o projeto pronto
 * e aprova antes de fechar.
 */
export default function DemoBanner({
  message = "Ola! Quero receber um site de demonstracao sem custo para a minha empresa.",
}: DemoBannerProps) {
  return (
    <section className="bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-secondary-200 bg-secondary-50 p-6 md:p-10">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary-500 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-background-50">
                  <i className="ri-gift-2-line" aria-hidden="true"></i>
                  Teste antes de decidir
                </span>
                <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                  Criamos um site de demonstração sem custo para a sua empresa aprovar.
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground-700">
                  Você vê o site pronto, funcionando e com a cara do seu negócio{" "}
                  <strong>antes de fechar qualquer coisa</strong>. Sem custo e sem compromisso:
                  aprovou, seguimos; não gostou, não tem problema.
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {["Sem custo", "Sem compromisso", "Você aprova primeiro", "Ajustes inclusos"].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-secondary-800"
                      >
                        <i className="ri-check-line text-secondary-600" aria-hidden="true"></i>
                        {tag}
                      </span>
                    )
                  )}
                </div>

                <a
                  href={whatsappLink(message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                  Quero ver uma demonstração
                </a>
              </div>

              <div className="rounded-2xl border border-secondary-200 bg-background-50 p-5 md:p-6">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-foreground-500">
                    Demonstração do seu site
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-semibold text-secondary-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-secondary-500"></span>
                    Pronto para você
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  <DemoRow icon="ri-layout-masonry-line" label="Seu site, no ar" value="Visual + estrutura" />
                  <DemoRow icon="ri-smartphone-line" label="Responsivo" value="Celular e desktop" />
                  <DemoRow icon="ri-search-eye-line" label="Rápido e otimizado" value="Pronto para o Google" />
                </div>
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-secondary-50 px-4 py-3">
                  <i className="ri-thumb-up-line text-lg text-secondary-600" aria-hidden="true"></i>
                  <p className="text-xs text-foreground-600">
                    Só seguimos depois da sua aprovação.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DemoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
        <i className={`${icon} text-lg`} aria-hidden="true"></i>
      </span>
      <div className="flex-1">
        <p className="text-xs text-foreground-500">{label}</p>
        <p className="text-sm font-semibold text-foreground-950">{value}</p>
      </div>
    </div>
  );
}